import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import chatImg from '../../assests/chat.jpg';
import avatar from '../../assests/logo.png';
import ChatTopbar from './ChatTopBar';
import SendMessageForm from './SendMessageForm';
import './Chat.css';
import socket from '../../utils/socket';
import moment from 'moment';
import axiosInstance from '../../axiosapi';
import { useSocketEvents } from '../../hook/useSocketEvents';

const ChatBox = ({ selectedStudent, selectedTeacher, onSuccess }) => {
  const [messages, setMessages] = useState([]);
  const messagePanelRef = useRef(null);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [typingUser, setTypingUser] = useState(null);

  const socketHandlers = useMemo(
    () => ({
      receiveMessage: (message) => {
        console.log('📩 Message received:', message);
        setMessages((prev) => [message, ...prev]);
        setShouldScrollToBottom(true); // Ensure we scroll down for a new message
      },
      userTyping: (data) => {
        console.log('✏️ Typing...', data);
        setTypingUser(data.username);
        setTimeout(() => setTypingUser(null), 2000);
      },
    }),
    [],
  );

  useSocketEvents(socket, socketHandlers);

  const fetchMessages = async (newPage) => {
    if (!selectedTeacher || !selectedStudent || loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const chatPanel = messagePanelRef.current;
      const previousScrollHeight = chatPanel.scrollHeight || 0; // Save scroll height before fetching

      const res = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/messages/${selectedStudent.id}/${selectedTeacher.id}?page=${newPage}&limit=10`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      );
      const newMessages = res.data.data;

      if (newMessages.length === 0) {
        setHasMore(false);
      } else {
        setMessages((prev) => [...prev, ...newMessages]); // Append messages
        setPage(newPage);

        // Restore previous scroll position after rendering
        setTimeout(() => {
          if (chatPanel) {
            chatPanel.scrollTop = chatPanel.scrollHeight - previousScrollHeight;
          }
        }, 100);
      }
    } catch (error) {
      console.error(error);
    }
    setLoadingMore(false);
  };

  useEffect(() => {
    if (!selectedTeacher || !selectedStudent) return;
    setMessages([]);
    setPage(1);
    setHasMore(true);
    fetchMessages(1);
  }, [selectedTeacher, selectedStudent]);

  // Scroll event handler
  const handleScroll = () => {
    if (!messagePanelRef.current || loadingMore || !hasMore) return;
    if (messagePanelRef.current.scrollTop === 0) {
      fetchMessages(page + 1);
    }
  };

  useEffect(() => {
    const chatPanel = messagePanelRef.current;
    if (chatPanel) {
      chatPanel.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (chatPanel) chatPanel.removeEventListener('scroll', handleScroll);
    };
  }, [messages, hasMore, loadingMore]);

  const handleNewMessage = (data) => {
    setShouldScrollToBottom(true);
    setMessages((prev) => [
      {
        senderId: selectedStudent.id,
        receiverId: selectedTeacher.id,
        content: data.content,
        created_at: data.created_at,
      },
      ...prev,
    ]);
  };

  useEffect(() => {
    if (!messagePanelRef.current) return;

    const panel = messagePanelRef.current;

    // Check if the user is already at the bottom
    const isAtBottom = panel.scrollHeight - (panel.scrollTop + panel.clientHeight) < 50;

    // Only scroll if a new message is sent OR the user was already at the bottom
    if (shouldScrollToBottom || isAtBottom) {
      panel.scrollTo({
        top: panel.scrollHeight,
        behavior: 'smooth',
      });
      setShouldScrollToBottom(false); // Reset flag after scrolling
    }
  }, [messages]);

  useEffect(() => {
    socket.emit('registerUser', selectedStudent.id);
  }, [selectedStudent]);

  const fileType = (file) => {
    if (file) {
      const extension = file.split('.').pop();
      if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
        return 'image';
      } else if (['mp4', 'webm', 'mkv', 'mov'].includes(extension)) {
        return 'video';
      } else {
        return 'document';
      }
    }
  };

  return (
    <>
      {!selectedTeacher ? (
        <div className="chat-main">
          <img src={chatImg} alt="chatImg" style={{ width: '300px' }} />
          <h2 className="chat-placeholder">Start a conversation with teacher</h2>
        </div>
      ) : (
        <div className="chat-window">
          <ChatTopbar selectedTeacher={selectedTeacher} />
          <div className="chat-messages" ref={messagePanelRef}>
            {loadingMore && <p style={{ textAlign: 'center' }}>Loading older messages...</p>}
            {messages.length > 0 ? (
              messages
                .slice()
                .reverse()
                .map((item, index) => {
                  const isSender = item.senderId === selectedStudent.id;
                  return (
                    <div key={index} className={`message ${isSender ? 'sender' : 'receiver'}`}>
                      <img
                        src={item.imageId ? item.imageId : avatar}
                        alt="avatar"
                        className={`${isSender ? 'hidden' : 'profile'}`}
                      />
                      <div className="message-details">
                        <div className="message-info">
                          <div className={`message-user ${isSender ? 'hidden' : ''}`}>
                            {item.first_name} {item.middle_name} {item.last_name}
                          </div>
                          <p className="message-time mb-0">{moment(item.created_at).format('MMM D h:mm A')}</p>
                        </div>
                        {item.file && (
                          <div className={`text-end`}>
                            <Link to={item.signedUrl} alt="file" target="_blank" rel="noreferrer">
                              {fileType(item.file) === 'image' ? (
                                <img src={item.signedUrl} alt="file" style={{ maxWidth: '50%' }} />
                              ) : fileType(item.file) === 'video' ? (
                                <video src={item.signedUrl} alt="file" controls style={{ maxWidth: '50%' }} />
                              ) : (
                                <p className="underline">{item.file}</p>
                              )}
                            </Link>
                          </div>
                        )}
                        {item.content && (
                          <div className={`message-content ${isSender ? 'sender' : 'receiver'}`}>{item.content}</div>
                        )}
                      </div>
                    </div>
                  );
                })
            ) : (
              <div className="chat-main">
                <img src={chatImg} alt="chatImg" style={{ width: '300px' }} />
                <h2 className="chat-placeholder">
                  No messages yet. Start a conversation with {selectedTeacher.first_name} {selectedTeacher.middle_name}{' '}
                  {selectedTeacher.last_name}
                </h2>
              </div>
            )}
            {typingUser && <div className="typing-indicator">{typingUser} is typing...</div>}
          </div>
          <SendMessageForm
            selectedTeacher={selectedTeacher}
            handleSuccess={(data) => {
              handleNewMessage(data);
              onSuccess();
            }}
          />
        </div>
      )}
    </>
  );
};

export default ChatBox;
